/**
 * A function to create a log object for the 'clickOnHidePlan' event (evid: 10)
 *
 * Event description: sent when a user clicks on hide plan
 *
 * @param {clickOnHidePlanParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - metasite id
 * @property {boolean} status - {hidden=T,unhidden=F}
 * @property {number} planID - plan_id
 * @property {string} formOrigin - place where from plan was hidden: bo, modal
 * @return {LogObject<clickOnHidePlanParams>} LogObject<clickOnHidePlanParams>
 */
export function clickOnHidePlan(params) {
    return { evid: 10, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'membershipPurchased' event (evid: 101)
 *
 * Event description: sent when a membership status is changed - mainly used for identifying new purchases
 *
 * @param {membershipPurchasedParams} params - The parameters for this event
 * @property {number} duration - plan duration in months
 * @property {DateTime} expirationDate - the date when the plans expires
 * @property {GUID} planGuid - plan_guid
 * @property {string} paymentStatus - approved, not approved, etc
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} uuid - owner_id
 * @property {number} price - plan price multiplied by 100
 * @property {GUID} membershipId - membership_id
 * @property {string} paymentType - {recurring,one-time}
 * @property {string} orderStatus - {Active,Pending, Suspended}
 * @property {string} paymentMethodType - credit card, pay pal, etc
 * @property {string} cancelInitiator - Owner, Member, PaymentFailure, SetupFailure
 * @property {string} user_agent - user_agent
 * @property {string} currency - currency
 * @property {DateTime} orderStartDate - order start date
 * @property {string} orderType - Online or Offline
 * @property {string} suspensions - suspension array if plan was suspended (and then resumed)
 * @property {string} periodUnit - weekly, yearly, monthly
 * @property {GUID} transactionId - cashier's transaction id
 * @property {GUID} siteMemberId - site_member_id
 * @return {LogObject<membershipPurchasedParams>} LogObject<membershipPurchasedParams>
 */
export function membershipPurchased(params) {
    return { evid: 101, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'provisionFromTemplatesStarted' event (evid: 102)
 *
 * Event description: Event is sent when provision from templates is started
 *
 * @param {provisionFromTemplatesStartedParams} params - The parameters for this event
 * @property {GUID} instanceId - instance id
 * @property {GUID} originInstanceId - instance id of template
 * @property {string} user_agent - user_agent
 * @property {GUID} msid - msid
 * @return {LogObject<provisionFromTemplatesStartedParams>} LogObject<provisionFromTemplatesStartedParams>
 */
export function provisionFromTemplatesStarted(params) {
    return { evid: 102, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'provisionFromTemplatesEnded' event (evid: 103)
 *
 * Event description: Provision from templates ended
 *
 * @param {provisionFromTemplatesEndedParams} params - The parameters for this event
 * @property {GUID} originInstanceId - template instance id
 * @property {string} user_agent - user_agent
 * @property {GUID} instanceid - instance id
 * @property {string} status - status of provision (Success/Failure)
 * @property {GUID} msid - msid
 * @return {LogObject<provisionFromTemplatesEndedParams>} LogObject<provisionFromTemplatesEndedParams>
 */
export function provisionFromTemplatesEnded(params) {
    return { evid: 103, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'currencyChanged' event (evid: 105)
 *
 * Event description: Event is send from server side when currency is changed
 *
 * @param {currencyChangedParams} params - The parameters for this event
 * @property {string} planTitle - Title of Plan
 * @property {number} duration - duration of plan
 * @property {GUID} planGuid - plan guid
 * @property {GUID} appInstanceId - app instance id
 * @property {string} paymentMode - payment mode (monthly/one time/yearly/weekly)
 * @property {number} price - price of plan
 * @property {string} user_agent - user_agent
 * @property {number} numOfBenefits - number of benefits
 * @property {string} currency - currency
 * @property {string} durationUnit - duration unit (month/year/week/none)
 * @property {GUID} msid - metasite guid
 * @return {LogObject<currencyChangedParams>} LogObject<currencyChangedParams>
 */
export function currencyChanged(params) {
    return { evid: 105, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boDealerCards' event (evid: 107)
 *
 * Event description: Fires when action is performed with dealer cards in the business manager of the Pricing Plans app.
 *
 * @param {boDealerCardsParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - biToken
 * @property {string} offerIdList - List of dealer card offers presented
 * @property {string} action - loaded/cta name/etc.
 * @return {LogObject<boDealerCardsParams>} LogObject<boDealerCardsParams>
 */
export function boDealerCards(params) {
    return { evid: 107, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'actionButtonClicked' event (evid: 11)
 *
 * Event description: sent when a user chooses an action under the action button. not sent for the action button click, but for the following click on a specific action
 *
 * @param {actionButtonClickedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - metasite id
 * @property {string} actionType - For actions that are made in bulk = bulk, the rest null.
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {number} planID - plan_id
 * @property {string} action - {edit/archive/hide} hide option exists only in OneApp
 * @property {string} formOrigin - where it was clicked from e.g. editor settings
 * @return {LogObject<actionButtonClickedParams>} LogObject<actionButtonClickedParams>
 */
export function actionButtonClicked(params) {
    return { evid: 11, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansPremiumStatusEnabled' event (evid: 1101)
 *
 * Event description: This event is created for User Onboarding Checklist. The aim is to identify if the user has/had a premium subscription on their sites.
 *
 * @param {pricingPlansPremiumStatusEnabledParams} params - The parameters for this event
 * @property {GUID} biToken - bItoken
 * @property {GUID} uuid - UUID
 * @property {GUID} subscriptionId - Premium subscription ID
 * @property {GUID} featureId - Premium feature ID that is linked to feature plan
 * @property {boolean} status - The event is designed to fire only when enabled = true
 * @property {GUID} appId - Id of an app that initialised the installation
 * @return {LogObject<pricingPlansPremiumStatusEnabledParams>} LogObject<pricingPlansPremiumStatusEnabledParams>
 */
export function pricingPlansPremiumStatusEnabled(params) {
    return { evid: 1101, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteWithPricingPlansDuplicated' event (evid: 1102)
 *
 * Event description: This event is created for User Onboarding Checklist. The aim is to identify if the site with plans was duplicated.
 *
 * @param {siteWithPricingPlansDuplicatedParams} params - The parameters for this event
 * @property {GUID} originMetaSiteId - Original msid
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - biToken = usually msid
 * @property {string} uuid - uuid
 * @property {GUID} appId - Id of an app that initialised the installation
 * @return {LogObject<siteWithPricingPlansDuplicatedParams>} LogObject<siteWithPricingPlansDuplicatedParams>
 */
export function siteWithPricingPlansDuplicated(params) {
    return { evid: 1102, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'offlineFlowContactWithTakenEmailAddress' event (evid: 113)
 *
 * Event description: The event is fired when a user tries to sell a plan to a contact, using an email address that already belongs to an existing site member.
 *
 * @param {offlineFlowContactWithTakenEmailAddressParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - Indicates where action was made
 * @property {GUID} biToken - msid
 * @property {string} origin - origin
 * @property {GUID} site_member_id - site_member_id of an existing member who's email is used.
 * @property {string} action - modal_opened/update_email/choose_existing_member
 * @return {LogObject<offlineFlowContactWithTakenEmailAddressParams>} LogObject<offlineFlowContactWithTakenEmailAddressParams>
 */
export function offlineFlowContactWithTakenEmailAddress(params) {
    return { evid: 113, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planSavedConfirmedSrc92Evid114' event (evid: 114)
 *
 * Event description: sent after a user clicks on 'save plan' (92:8) and is confirmed by server
 *
 * @param {planSavedConfirmedSrc92Evid114Params} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} planTitle - plan title
 * @property {string} integratedVerticals - array of verticals that are being integrated
 * @property {number} duration - plan duration in months
 * @property {string} referralInfo - referral info
 * @property {string} billingdurationUnit - billing duration unit type (week/month/year)
 * @property {string} planPricingType - one of: subscription, single payment for duration, single payment unlimited
 * @property {boolean} allowCancel - allow subscriptions cancellations: true/false
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - metasite id
 * @property {boolean} disclaimerPolicyCheckbox - Plan policy (disclaimer) enabled/disabled
 * @property {boolean} customThankYouEnabled - Custom thank you page enabled
 * @property {number} purchaseLimit - limit  of purchases for plan
 * @property {string} paymentMode - {one time, monthly}
 * @property {number} price - plan price
 * @property {string} setupFeeName - Setup fee name. Since Aug 2023
 * @property {boolean} isTaglineFilled - tag line filled {T/F}
 * @property {string} feeAmount - fee amount
 * @property {GUID} customCheckoutFormGuid - If custom checkout form enabled at plan level. Custom form guid links to table:
 * @property {string} payment - price
 * @property {number} billingcyclecount - number of billing cycles
 * @property {boolean} hasDisclaimer - true if the disclaimer is used; false otherwise
 * @property {string} origin - {paid plans, bookings,pages} or other relevant vertical
 * @property {number} numOfBenefits - number of benefits defined by the user
 * @property {string} currency - currency
 * @property {string} durationUnit - weeks, months, years
 * @property {string} form_type - Form type defined by initiating vertical. Starting from Q3 2022
 * @property {number} freeTrialDays - trial duration in days
 * @property {boolean} disclaimerCheckedByDefault - Plan policy (disclaimer) value checked by default in checkout enabled/disabled
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {number} billingDurationCount - billing duration count
 * @property {string} formFields - Form configuration (fields) that is loaded with the form. Null values stand for standard form, with standard fields. Starting from Q3 2022
 * @property {boolean} allowCustomStart - allowing custom start date: true/false
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr)
 * @return {LogObject<planSavedConfirmedSrc92Evid114Params>} LogObject<planSavedConfirmedSrc92Evid114Params>
 */
export function planSavedConfirmedSrc92Evid114(params) {
    return { evid: 114, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uoUFunnelPlanCustomisationPageSrc92Evid115' event (evid: 115)
 *
 * Event description: All events related to plan customisation page
 *
 * @param {uoUFunnelPlanCustomisationPageSrc92Evid115Params} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} widgetType - widget type that initiated loading of this page. (list, featured and button).
 * @property {string} origin - origin
 * @property {GUID} site_member_id - site_member_id
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {GUID} couponId - Coupon id if coupon was applied at checkout
 * @property {string} referralinfo - indicates where action was made
 * @property {string} isCustomStartDateEnabled - Indicates if plan had custom start date feature enabled at the time when plan was selected.
 * @property {string} isCustomCheckoutFormEnabled - Indicates if plan had custom ch eckout form enabled
 * @property {string} isGuestCheckoutEnabled - Indicates if site has guest checkout enabled at the time when plan was selected.
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @property {string} actionName - start_load/finish_load/continue/{error_code}
 * @property {string} actionGroup - load/click/error
 * @property {GUID} checkoutFlowId - internal ID by Pricing Plans
 * @return {LogObject<uoUFunnelPlanCustomisationPageSrc92Evid115Params>} LogObject<uoUFunnelPlanCustomisationPageSrc92Evid115Params>
 */
export function uoUFunnelPlanCustomisationPageSrc92Evid115(params) {
    return { evid: 115, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uoUFunnelPlanSummaryPageMobileAppSrc92Evid116' event (evid: 116)
 *
 * Event description: All events related to plan summary page
 *
 * @param {uoUFunnelPlanSummaryPageMobileAppSrc92Evid116Params} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} widgetType - widget type that initiated loading of this page. (list, featured and button).
 * @property {string} origin - origin
 * @property {GUID} site_member_id - site_member_id
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {GUID} couponId - Coupon id if coupon was applied at checkout
 * @property {string} referralinfo - indicates where action was made
 * @property {string} isCustomStartDateEnabled - Indicates if plan had custom start date feature enabled at the time when plan was selected.
 * @property {string} isCustomCheckoutFormEnabled - Indicates if plan had custom ch eckout form enabled
 * @property {string} isGuestCheckoutEnabled - Indicates if site has guest checkout enabled at the time when plan was selected.
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @property {string} actionName - start_load/finish_load/continue/{error_code}
 * @property {string} actionGroup - load/click/error
 * @property {GUID} checkoutFlowId - internal ID by Pricing Plans
 * @return {LogObject<uoUFunnelPlanSummaryPageMobileAppSrc92Evid116Params>} LogObject<uoUFunnelPlanSummaryPageMobileAppSrc92Evid116Params>
 */
export function uoUFunnelPlanSummaryPageMobileAppSrc92Evid116(params) {
    return { evid: 116, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pageInstalledSrc92Evid117' event (evid: 117)
 *
 * Event description: Event is fired after page is installed on site
 *
 * @param {pageInstalledSrc92Evid117Params} params - The parameters for this event
 * @property {string} tpaPageId - Page ID the got installed
 * @property {string} pageTitle - page title that is installed
 * @property {GUID} metasiteId - msid of the site
 * @return {LogObject<pageInstalledSrc92Evid117Params>} LogObject<pageInstalledSrc92Evid117Params>
 */
export function pageInstalledSrc92Evid117(params) {
    return { evid: 117, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'priceChangeActionSrc92Evid120' event (evid: 120)
 *
 * Event description: When an action is performed related to price change feature
 *
 * @param {priceChangeActionSrc92Evid120Params} params - The parameters for this event
 * @property {string} plan_guid - plan_guid
 * @property {string} additional_info - additional_info
 * @return {LogObject<priceChangeActionSrc92Evid120Params>} LogObject<priceChangeActionSrc92Evid120Params>
 */
export function priceChangeActionSrc92Evid120(params) {
    return { evid: 120, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'priceChangeModalActionsSrc92Evid121' event (evid: 121)
 *
 * Event description: Price change modal actions
 *
 * @param {priceChangeModalActionsSrc92Evid121Params} params - The parameters for this event
 * @property {string} plan_guid - plan_guid
 * @property {string} action - open_modal, cancel, change_price
 * @property {string} additional_info - e.g.
 * {
 *   "price_before": 15,
 *   "price_after": 30,
 *   "applies_to": "existing_and_new_subscriptions",
 *   "subscriptions_affected": 134
 * }
 *
 * @return {LogObject<priceChangeModalActionsSrc92Evid121Params>} LogObject<priceChangeModalActionsSrc92Evid121Params>
 */
export function priceChangeModalActionsSrc92Evid121(params) {
    return { evid: 121, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dummyName' event (evid: 1300)
 *
 * Event description: dummy-name.
 *
 * @param {dummyNameParams} params - The parameters for this event
 * @return {LogObject<dummyNameParams>} LogObject<dummyNameParams>
 */
export function dummyName(params) {
    return { evid: 1300, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'settingsOpened' event (evid: 15)
 *
 * Event description: Sent when settings panel on the editor is opened.
 *
 * @param {settingsOpenedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - place where it was navigated form
 * @property {GUID} biToken - metasite id
 * @property {string} editor_view_mode - Desktop/mobile
 * @return {LogObject<settingsOpenedParams>} LogObject<settingsOpenedParams>
 */
export function settingsOpened(params) {
    return { evid: 15, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'plansPageView' event (evid: 16)
 *
 * Event description: Fires when a page with PP widget gets loaded. This does not necessarily mean that the widget itself was loaded.
 *
 * @param {plansPageViewParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {string} referral_info - {bookings}
 * @property {GUID} visitor_id - visitor_id
 * @property {string} widgetType - widget type: list, featured and button,
 * @property {GUID} site_member_id - site_member_id
 * @property {string} referralId - ID of the referring item - blog post id, event id, form category id
 * @property {string} pageName - The name of the page where widget was loaded
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @return {LogObject<plansPageViewParams>} LogObject<plansPageViewParams>
 */
export function plansPageView(params) {
    return { evid: 16, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planPurchaseClick' event (evid: 17)
 *
 * Event description: Fires when plan is selected (clicked) in the plan picker.
 *
 * @param {planPurchaseClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} widgetType - widget type: list, featured and button,
 * @property {string} origin - origin
 * @property {GUID} site_member_id - site_member_id
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {GUID} couponId - Coupon id if coupon was applied at checkout
 * @property {string} referralinfo - indicates where action was made
 * @property {string} isCustomStartDateEnabled - Indicates if plan had custom start date feature enabled at the time when plan was selected.
 * @property {string} isCustomCheckoutFormEnabled - Indicates if plan had custom ch eckout form enabled
 * @property {string} isGuestCheckoutEnabled - Indicates if site has guest checkout enabled at the time when plan was selected.
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @property {GUID} checkoutFlowId - internal ID by Pricing Plans
 * @return {LogObject<planPurchaseClickParams>} LogObject<planPurchaseClickParams>
 */
export function planPurchaseClick(params) {
    return { evid: 17, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'membershipStatusLoaded' event (evid: 18)
 *
 * Event description: sent when memberships status loads after purchase. (Basically thank you page )
 *
 * @param {membershipStatusLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {GUID} membershipId - membership_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} planID - plan_id
 * @property {string} membershipStatus - {approved,pending,denied} etc
 * @property {string} isCustomCheckoutFormEnabled - Indicates if plan had custom ch eckout form enabled
 *
 * @property {string} isCustomStartDateEnabled - Indicates if plan had custom start date feature enabled at the time when plan was selected.
 *
 * @property {string} isGuestCheckoutEnabled - Indicates if site has guest checkout enabled at the time when plan was selected.
 *
 * @property {string} checkoutFlowId - internal ID by Pricing Plans
 *
 * @property {string} referralInfo - indicates where action originated from
 * @property {string} checkoutVersion - Indicates checkout version
 * @return {LogObject<membershipStatusLoadedParams>} LogObject<membershipStatusLoadedParams>
 */
export function membershipStatusLoaded(params) {
    return { evid: 18, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ppWidgetLoaded' event (evid: 19)
 *
 * Event description: Fires when PP widget is loaded . This is a case when UoU does not scroll enough to see the widget. Initial event is 92:16
 *
 * @param {ppWidgetLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {string} referral_info - {bookings}
 * @property {GUID} visitor_id - visitor_id
 * @property {string} widgetType - widget type: list, featured and button,
 * @property {GUID} site_member_id - site_member_id
 * @property {string} referralId - ID of the referring item - blog post id, event id, form category id
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @return {LogObject<ppWidgetLoadedParams>} LogObject<ppWidgetLoadedParams>
 */
export function ppWidgetLoaded(params) {
    return { evid: 19, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'managePlansLoaded' event (evid: 2)
 *
 * Event description: sent when the 'manage plans' in BO is loaded SF. referralInfo = internal when User comes back e.g. plan creation form
 *
 * @param {managePlansLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referralInfo = internal when User comes back e.g. plan creation form
 * @property {string} screenName - welcome_screen; onboarding_checklist;
 * @property {GUID} biToken - metasite id
 * @property {string} origin - {BizMgr,Editor,other app name}
 * @property {number} planCount - Number of plans (non-archived) on load
 * @property {number} planCountArchived - Number of archived plans on load
 * @return {LogObject<managePlansLoadedParams>} LogObject<managePlansLoadedParams>
 */
export function managePlansLoaded(params) {
    return { evid: 2, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planPurchased' event (evid: 20)
 *
 * Event description: sent when purchased was made
 *
 * @param {planPurchasedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {number} duration - plan duration in months
 * @property {GUID} planGuid - plan_guid
 * @property {string} paymentStatus - approved, not approved, etc
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - metasite id
 * @property {number} price - plan price
 * @property {GUID} membershipId - membership_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} setupFeeName - Setup fee name. Since Aug 2023
 * @property {string} paymentType - {recurring,one-time}
 * @property {string} feeAmount - fee amount
 * @property {string} paymentMethodType - credit card, pay pal, etc
 * @property {string} payment - price in string
 * @property {string} currency - currency
 * @property {GUID} site_member_id - site_member_id
 * @property {string} feeName - fee name
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {GUID} couponId - Coupon id if coupon was applied at checkout
 * @property {GUID} transactionId - cashier's transaction_id
 * @property {GUID} checkoutFlowId - internal ID by Pricing Plans
 * @property {string} isGuestCheckoutEnabled - Indicates if site has guest checkout enabled at the time when plan was selected.
 * @property {string} isCustomStartDateEnabled - Indicates if plan had custom start date feature enabled at the time when plan was selected.
 * @property {string} isCustomCheckoutFormEnabled - Indicates if plan had custom ch eckout form enabled
 * @property {string} referral_info - identification where UoU flow originated
 *
 * @return {LogObject<planPurchasedParams>} LogObject<planPurchasedParams>
 */
export function planPurchased(params) {
    return { evid: 20, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myPaidPlansPageLoaded' event (evid: 23)
 *
 * Event description: When My Paid plans page on OneApp was loaded successfully
 *
 * @param {myPaidPlansPageLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @return {LogObject<myPaidPlansPageLoadedParams>} LogObject<myPaidPlansPageLoadedParams>
 */
export function myPaidPlansPageLoaded(params) {
    return { evid: 23, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myPaidPlansSwitchTab' event (evid: 24)
 *
 * Event description: When the user switches between Active and expired tabs
 *
 * @param {myPaidPlansSwitchTabParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {string} currentTab - Active/Expired
 * @property {string} targetTab - Active/Expired
 * @return {LogObject<myPaidPlansSwitchTabParams>} LogObject<myPaidPlansSwitchTabParams>
 */
export function myPaidPlansSwitchTab(params) {
    return { evid: 24, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'memberProfilePaidPlansLoaded' event (evid: 25)
 *
 * Event description: When the paid plans page of a DP member profile page was loaded
 *
 * @param {memberProfilePaidPlansLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @return {LogObject<memberProfilePaidPlansLoadedParams>} LogObject<memberProfilePaidPlansLoadedParams>
 */
export function memberProfilePaidPlansLoaded(params) {
    return { evid: 25, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'memberProfilePaidPlansSwitchTabs' event (evid: 26)
 *
 * Event description: When the user switches between Active and Expired tabs on the paid plans page on a member profile page
 *
 * @param {memberProfilePaidPlansSwitchTabsParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {string} currentTab - Active/Expired
 * @property {string} targetTab - Active/Expired
 * @return {LogObject<memberProfilePaidPlansSwitchTabsParams>} LogObject<memberProfilePaidPlansSwitchTabsParams>
 */
export function memberProfilePaidPlansSwitchTabs(params) {
    return { evid: 26, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paidPlansAddPurchaseClick' event (evid: 29)
 *
 * Event description: When the user(owner) clicks on add purchase in order to add a purchase manually (Sell a pricing plan to this contact).
 *
 * @param {paidPlansAddPurchaseClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - Indicated where action was made
 * @property {GUID} biToken - msid
 * @property {GUID} originAppId - app_id of vertical clicked at cta
 * @property {string} ctaType - cta_type
 * @property {string} origin - contacts panel, purchased plans page
 * @return {LogObject<paidPlansAddPurchaseClickParams>} LogObject<paidPlansAddPurchaseClickParams>
 */
export function paidPlansAddPurchaseClick(params) {
    return { evid: 29, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paidPlansAddPurchaseChooseClientAddNewClient' event (evid: 30)
 *
 * Event description: When the user (owner) selects client in order to add a purchase for him
 *
 * @param {paidPlansAddPurchaseChooseClientAddNewClientParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - Indicates where action was made
 * @property {GUID} biToken - msid
 * @property {string} origin - Origin
 * @property {GUID} site_member_id - site_member_id
 * @property {boolean} isNewMember - true or false
 * @return {LogObject<paidPlansAddPurchaseChooseClientAddNewClientParams>} LogObject<paidPlansAddPurchaseChooseClientAddNewClientParams>
 */
export function paidPlansAddPurchaseChooseClientAddNewClient(params) {
    return { evid: 30, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'offlineFlowChooseAPlanForAMember' event (evid: 31)
 *
 * Event description: When the user (owner) select a plan for owner
 *
 * @param {offlineFlowChooseAPlanForAMemberParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - Indicated where action was made
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - msid
 * @property {string} origin - origin
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<offlineFlowChooseAPlanForAMemberParams>} LogObject<offlineFlowChooseAPlanForAMemberParams>
 */
export function offlineFlowChooseAPlanForAMember(params) {
    return { evid: 31, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'membershipMarkedAsPaid' event (evid: 33)
 *
 * Event description: When the user (owner) marks membership as paid. Including MOTO flow, where UoU is charged by User.
 *
 * @param {membershipMarkedAsPaidParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - Indicates where action was made
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - msid
 * @property {string} origin - origin
 * @property {boolean} status - T-Paid, F-Unpaid
 * @property {GUID} site_member_id - site_member_id
 * @property {DateTime} startDate - order start date
 * @property {string} action - complete/complete-confirm
 * @return {LogObject<membershipMarkedAsPaidParams>} LogObject<membershipMarkedAsPaidParams>
 */
export function membershipMarkedAsPaid(params) {
    return { evid: 33, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planSavedConfirmed' event (evid: 38)
 *
 * Event description: sent after a user clicks on 'save plan' (92:8) and is confirmed by server
 *
 * @param {planSavedConfirmedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} planTitle - plan_title
 * @property {string} integratedVerticals - integrated_verticals
 * @property {number} duration - duration
 * @property {string} referralInfo - referral_info
 * @property {string} billingdurationUnit - billing_duration_unit
 * @property {string} planPricingType - plan_pricing_type
 * @property {boolean} allowCancel - allow_cancel
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - msid
 * @property {boolean} disclaimerPolicyCheckbox - disclaimer_policy_checkbox
 * @property {boolean} customThankYouEnabled - custom_thank_you_enabled
 * @property {number} purchaseLimit - purchase_limit
 * @property {string} paymentMode - payment_mode
 * @property {number} price - price
 * @property {string} setupFeeName - setup_fee_name
 * @property {boolean} isTaglineFilled - is_tagline_filled
 * @property {string} feeAmount - fee_amount
 * @property {GUID} customCheckoutFormGuid - custom_checkout_form_guid
 * @property {string} payment - payment
 * @property {number} billingcyclecount - billing_cycle_count
 * @property {boolean} hasDisclaimer - has_disclaimer
 * @property {string} origin - origin
 * @property {number} numOfBenefits - num_of_benefits
 * @property {string} currency - currency
 * @property {string} durationUnit - duration_unit
 * @property {string} form_type - form_type
 * @property {number} freeTrialDays - free_trial_days
 * @property {boolean} disclaimerCheckedByDefault - disclaimer_checked_by_default
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - setup_fee_amount
 * @property {number} billingDurationCount - billing_duration_count
 * @property {string} formFields - form_fields
 * @property {boolean} allowCustomStart - allow_custom_start
 * @property {string} formOrigin - form_origin
 * @return {LogObject<planSavedConfirmedParams>} LogObject<planSavedConfirmedParams>
 */
export function planSavedConfirmed(params) {
    return { evid: 38, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'benefitsTransactionAdded' event (evid: 405)
 *
 * Event description: Benefit transaction change event. Identifies benefit transaction information.
 *
 * General note: After moving to BASS, starting from 2022 Q3. For benefit transaction changed, there will be a separate event.
 *
 * @param {benefitsTransactionAddedParams} params - The parameters for this event
 * @property {GUID} instanceId - Instance ID
 * @property {GUID} originalTransactionId - Original Transaction ID
 * @property {GUID} resourceGuid - Resource ID
 * @property {GUID} benefitId - Benefit ID
 * @property {number} balance - Balance
 * @property {string} idempotencyKey - Idempotency Key
 * @property {number} amount - Amount
 * @property {GUID} pricingPlanId - Pricing Plan ID
 * @property {GUID} planorderId - Plan Order ID
 * @property {GUID} id - Entry ID
 * @property {DateTime} creationDate - Creation Date
 * @property {GUID} memberId - Member ID
 * @property {string} action - Action status
 * @return {LogObject<benefitsTransactionAddedParams>} LogObject<benefitsTransactionAddedParams>
 */
export function benefitsTransactionAdded(params) {
    return { evid: 405, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteAddedToAPopulationSrc92Evid406' event (evid: 406)
 *
 * Event description: The event is triggered once a site is moved into a new population. The purpose is to track sites for Benefit Programs migration purposes.
 *
 * @param {siteAddedToAPopulationSrc92Evid406Params} params - The parameters for this event
 * @property {GUID} instanceId - Instance ID
 * @property {string} action - Added/removed from the population
 * @property {string} reason - to help for BED to distinguish between automatic, immigrator job, etc.
 * @property {string} populationId - Population ID
 * @return {LogObject<siteAddedToAPopulationSrc92Evid406Params>} LogObject<siteAddedToAPopulationSrc92Evid406Params>
 */
export function siteAddedToAPopulationSrc92Evid406(params) {
    return { evid: 406, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansServerAuditLogSrc92Evid407' event (evid: 407)
 *
 * Event description: Pricing Plans audit log entry
 *
 * @param {pricingPlansServerAuditLogSrc92Evid407Params} params - The parameters for this event
 * @property {string} message - Message
 * @property {string} entityType - Describes the type used for entityId
 * @property {string} entityId - Entity ID
 * @property {string} data - Data json
 * @property {GUID} meta_site_id - additional msid
 * @return {LogObject<pricingPlansServerAuditLogSrc92Evid407Params>} LogObject<pricingPlansServerAuditLogSrc92Evid407Params>
 */
export function pricingPlansServerAuditLogSrc92Evid407(params) {
    return { evid: 407, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansClientAuditLogSrc92Evid408' event (evid: 408)
 *
 * Event description: Pricing Plans audit log entry
 *
 * @param {pricingPlansClientAuditLogSrc92Evid408Params} params - The parameters for this event
 * @property {string} message - Message
 * @property {string} entityType - Describes the type used for entityId
 * @property {string} entityId - Entity ID
 * @property {string} data - Data json
 * @property {GUID} meta_site_id - additional msid
 * @return {LogObject<pricingPlansClientAuditLogSrc92Evid408Params>} LogObject<pricingPlansClientAuditLogSrc92Evid408Params>
 */
export function pricingPlansClientAuditLogSrc92Evid408(params) {
    return { evid: 408, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansAppProvisionEventSrc92Evid409' event (evid: 409)
 *
 * Event description: The event is fired after event for provisioning is received and before making app installation. In other words it could be seen as initiating installation.
 *
 * @param {pricingPlansAppProvisionEventSrc92Evid409Params} params - The parameters for this event
 * @property {string} uuid - uuid if there is one
 * @property {GUID} originalMsid - If added as a result of site duplication
 * @property {string} metaSiteId - msid as explcit field
 * @return {LogObject<pricingPlansAppProvisionEventSrc92Evid409Params>} LogObject<pricingPlansAppProvisionEventSrc92Evid409Params>
 */
export function pricingPlansAppProvisionEventSrc92Evid409(params) {
    return { evid: 409, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'preModalOpened' event (evid: 41)
 *
 * Event description: sent when the pre modal is loaded
 *
 * @param {preModalOpenedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - metasite id
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} formOrigin - indicates where the pre-modal was opened {bookings modal, blog modal, etc)
 * @return {LogObject<preModalOpenedParams>} LogObject<preModalOpenedParams>
 */
export function preModalOpened(params) {
    return { evid: 41, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectModalOpened' event (evid: 43)
 *
 * Event description: sent when connect modal is opened
 *
 * @param {connectModalOpenedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referral info
 * @property {GUID} biToken - metasite id
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {number} planCount - The number of plans (non-archived) on load.
 * @property {number} planCountArchived - Number of archived plans on load
 * @property {string} formOrigin - indicates where the form was opened {bookings, blog, forum  etc)
 * @return {LogObject<connectModalOpenedParams>} LogObject<connectModalOpenedParams>
 */
export function connectModalOpened(params) {
    return { evid: 43, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectPlanClick' event (evid: 44)
 *
 * Event description: sent when User clicks on Connect button to connect a plan to a service/post/etc (in connect modal from vertical side)
 *
 * @param {connectPlanClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan ID
 * @property {GUID} biToken - metasite ID
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} value - indicates if the plan was connected or disconnected {connect/disconnect/save (WixApp)}
 * @property {string} formOrigin - indicates where the form was opened {bookings, blog, events etc)
 * @return {LogObject<connectPlanClickParams>} LogObject<connectPlanClickParams>
 */
export function connectPlanClick(params) {
    return { evid: 44, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutStage' event (evid: 46)
 *
 * Event description: sent when UoU goes through different stages of the checkout flow
 *
 * @param {checkoutStageParams} params - The parameters for this event
 * @property {GUID} appInstanceId - App instance ID
 * @property {boolean} guestCheckout - Was guest checkout enabled on site at the time of the event?
 * @property {string} stage - stages of the checkout process {checkout screen loaded, auth-method-signup, auth-method-login, etc}
 * @property {GUID} planGuid - plan guid
 * @property {GUID} biToken - msid
 * @property {GUID} checkoutFlowId - internal ID by Pricing Plans
 * @property {string} isCustomStartDateEnabled - Indicates if plan had custom start date feature enabled at the time when plan was selected.
 * @property {string} isCustomCheckoutFormEnabled - Indicates if plan had custom ch eckout form enabled
 * @property {string} referralInfo - indicates where action was made
 * @property {string} debugData - information for debugging
 * @return {LogObject<checkoutStageParams>} LogObject<checkoutStageParams>
 */
export function checkoutStage(params) {
    return { evid: 46, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'upgradeReferralClick' event (evid: 47)
 *
 * Event description: sent when a User clicks on a link to Upgrade to Premium
 *
 * @param {upgradeReferralClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app id
 * @property {string} referralInfo - e.g. {toast, modal, BO}
 * @property {GUID} biToken - msid
 * @property {boolean} isPreview - indicates if clicked in preview mode
 * @return {LogObject<upgradeReferralClickParams>} LogObject<upgradeReferralClickParams>
 */
export function upgradeReferralClick(params) {
    return { evid: 47, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'moreActionsItemClick' event (evid: 48)
 *
 * Event description: sent when user clicks on one of the items in the more actions list
 *
 * @param {moreActionsItemClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app id
 * @property {GUID} biToken - msid
 * @property {string} origin - dashboard/editor
 * @property {string} actionName - {new purchase,email automations, manage payments}
 * @property {string} referralinfo - place where click came from
 * @return {LogObject<moreActionsItemClickParams>} LogObject<moreActionsItemClickParams>
 */
export function moreActionsItemClick(params) {
    return { evid: 48, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createPlanClick' event (evid: 5)
 *
 * Event description: sent when the user clicks 'create a plan'
 *
 * @param {createPlanClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referrer info
 * @property {GUID} biToken - metasite id
 * @property {string} origin - origin
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr, etc)
 * @return {LogObject<createPlanClickParams>} LogObject<createPlanClickParams>
 */
export function createPlanClick(params) {
    return { evid: 5, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectionIntent' event (evid: 50)
 *
 * Event description: sent when user expresses intent to connect a plan to another service
 *
 * @param {connectionIntentParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app id
 * @property {GUID} planGuid - plan guid
 * @property {GUID} biToken - msid
 * @property {string} serviceName - {bookings, challenges, forum, etc}
 * @property {string} intentName - {connect or manage for WixApp only}
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} benefits_system - This field helps to understand if event was fired under benefits v2, or benefits program, or whatever will come in the future.
 * @return {LogObject<connectionIntentParams>} LogObject<connectionIntentParams>
 */
export function connectionIntent(params) {
    return { evid: 50, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectedServicesUpdated' event (evid: 51)
 *
 * Event description: sent when user successfully updates services connected to a plan
 *
 * @param {connectedServicesUpdatedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app id
 * @property {GUID} planGuid - plan guid
 * @property {GUID} biToken - msid
 * @property {string} serviceName - {bookings,challenges, forum, etc}
 * @property {string} benefits_system - This field helps to understand if event was fired under benefits v2, or benefits program, or whatever will come in the future.
 * @return {LogObject<connectedServicesUpdatedParams>} LogObject<connectedServicesUpdatedParams>
 */
export function connectedServicesUpdated(params) {
    return { evid: 51, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'thankYouPageCtaButtonClick' event (evid: 52)
 *
 * Event description: Event used to track Thank You page CTA button click.
 *
 * @param {thankYouPageCtaButtonClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {string} buttonName - CTA button name at Thank You page
 * @property {string} referral_info - identification where UoU flow originated
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @property {string} checkoutFlowId - internal ID by Pricing Plans
 * @return {LogObject<thankYouPageCtaButtonClickParams>} LogObject<thankYouPageCtaButtonClickParams>
 */
export function thankYouPageCtaButtonClick(params) {
    return { evid: 52, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'creationFormCtaClick' event (evid: 53)
 *
 * Event description: sent when Connect CTA clicked anywhere within Pricing Plans. Used for promoting Connect Payment
 *
 * @param {creationFormCtaClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - referral_info
 * @property {GUID} biToken - msid
 * @property {string} origin - dashboard/ editor
 * @property {string} action - name of the action {connect payment, see more options}
 * @return {LogObject<creationFormCtaClickParams>} LogObject<creationFormCtaClickParams>
 */
export function creationFormCtaClick(params) {
    return { evid: 53, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiAssistantActions' event (evid: 54)
 *
 * Event description: Events that relate to AI assistant for plan creation
 *
 * @param {aiAssistantActionsParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - referral_info
 * @property {GUID} biToken - msid
 * @property {string} origin - origin
 * @property {string} formFields - form_fields
 * @property {string} formOrigin - form_origin
 * @property {string} action_name - Action description
 * @return {LogObject<aiAssistantActionsParams>} LogObject<aiAssistantActionsParams>
 */
export function aiAssistantActions(params) {
    return { evid: 54, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planSettingsPanelOpen' event (evid: 55)
 *
 * Event description: Sent when plan settings panel opens currently from widget only
 *
 * @param {planSettingsPanelOpenParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} widgetId - id of widget
 * @property {GUID} biToken - msid
 * @property {string} widgetType - pre-widget, list, single, button - widget types
 * @return {LogObject<planSettingsPanelOpenParams>} LogObject<planSettingsPanelOpenParams>
 */
export function planSettingsPanelOpen(params) {
    return { evid: 55, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiAssistantPlanCreated' event (evid: 56)
 *
 * Event description: Sent when plan is created with AI assitant
 *
 * @param {aiAssistantPlanCreatedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} planTitle - plan_title
 * @property {string} integratedVerticals - integrated_verticals
 * @property {number} duration - duration
 * @property {string} referralInfo - referral_info
 * @property {string} billingdurationUnit - billing_duration_unit
 * @property {string} galleryCardTemplateID - gallery_card_template_id
 * @property {string} planPricingType - plan_pricing_type
 * @property {boolean} allowCancel - allow_cancel
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - msid
 * @property {boolean} disclaimerPolicyCheckbox - disclaimer_policy_checkbox
 * @property {boolean} customThankYouEnabled - custom_thank_you_enabled
 * @property {number} purchaseLimit - purchase_limit
 * @property {string} paymentMode - payment_mode
 * @property {number} price - price
 * @property {string} setupFeeName - setup_fee_name
 * @property {boolean} isTaglineFilled - is_tagline_filled
 * @property {string} feeAmount - fee_amount
 * @property {GUID} customCheckoutFormGuid - custom_checkout_form_guid
 * @property {string} payment - payment
 * @property {number} billingcyclecount - billing_cycle_count
 * @property {boolean} hasDisclaimer - has_disclaimer
 * @property {string} origin - origin
 * @property {number} numOfBenefits - num_of_benefits
 * @property {string} currency - currency
 * @property {string} durationUnit - duration_unit
 * @property {string} form_type - form_type
 * @property {number} freeTrialDays - free_trial_days
 * @property {boolean} disclaimerCheckedByDefault - disclaimer_checked_by_default
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - setup_fee_amount
 * @property {number} billingDurationCount - billing_duration_count
 * @property {string} formFields - form_fields
 * @property {boolean} allowCustomStart - allow_custom_start
 * @property {string} formOrigin - form_origin
 * @return {LogObject<aiAssistantPlanCreatedParams>} LogObject<aiAssistantPlanCreatedParams>
 */
export function aiAssistantPlanCreated(params) {
    return { evid: 56, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetClickOnPlanHide' event (evid: 57)
 *
 * Event description: When user clicks hide/unhide plan checkbox in Editor widget settings
 *
 * @param {widgetClickOnPlanHideParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} widgetId - widget id
 * @property {GUID} planGuid - plan guid
 * @property {GUID} biToken - msid
 * @property {boolean} hidden - hidden: true/false
 * @property {string} widgetType - pre-widget, list, single, button - widget types
 * @return {LogObject<widgetClickOnPlanHideParams>} LogObject<widgetClickOnPlanHideParams>
 */
export function widgetClickOnPlanHide(params) {
    return { evid: 57, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetPlanOrderChanged' event (evid: 58)
 *
 * Event description: When User changes plan order in Editor widget settings
 *
 * @param {widgetPlanOrderChangedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} widgetId - widget id
 * @property {GUID} biToken - msid
 * @property {string} widgetType - pre-widget, list, single, button - widget types
 * @return {LogObject<widgetPlanOrderChangedParams>} LogObject<widgetPlanOrderChangedParams>
 */
export function widgetPlanOrderChanged(params) {
    return { evid: 58, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansTaxToggleSwitch' event (evid: 59)
 *
 * Event description: sent on tax toggle switch by User. This indicates intent to add/change tax settings.
 *
 * @param {pricingPlansTaxToggleSwitchParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {string} value - {on/off}
 * @return {LogObject<pricingPlansTaxToggleSwitchParams>} LogObject<pricingPlansTaxToggleSwitchParams>
 */
export function pricingPlansTaxToggleSwitch(params) {
    return { evid: 59, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planFormOpened' event (evid: 6)
 *
 * Event description: sent when the plan form page is loaded SF
 *
 * @param {planFormOpenedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referral info
 * @property {string} galleryCardTemplateID - identification of gallery card that user selected
 * @property {GUID} biToken - metasite id
 * @property {string} gallerySiteCategory - Site category used to prioritise gallery images
 * @property {string} origin - dashboard/editor
 * @property {string} form_type - Form type defined by initiating vertical. Starting from Q3 2022. null = default_form
 * @property {string} formFields - Form configuration (fields) that is loaded with the form. Null values stand for standard form, with standard fields. Starting from Q3 2022
 * @property {boolean} is_archived - indicates if the plan viewed is archived
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr, etc)
 * @return {LogObject<planFormOpenedParams>} LogObject<planFormOpenedParams>
 */
export function planFormOpened(params) {
    return { evid: 6, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansTaxSettingsSaved' event (evid: 60)
 *
 * Event description: The event is fired when a User saves settings in the Pricing Plans settings. In May 2023, due to autosave, this event is fired at each toggle swtich/change event.
 *
 * @param {pricingPlansTaxSettingsSavedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} guestCheckout - Guest checkout enabled/disabled Feature released end of Sep '22
 * @property {boolean} policyCheckboxCheckedByDefaultToggle - Policy checkbox checked by default enabled/disabled
 * @property {GUID} biToken - msid
 * @property {boolean} toggle - TAX toggle on or off
 * @property {number} taxRate - tax rate e.g. if 20% show as '20'
 * @property {string} taxType - type of tax {included/added}
 * @property {string} tabName - Tab name where settings were saved (Implemented in May 2023) Ended with ecom integration
 * @property {boolean} policyCheckboxToggle - Policy checkbox enabled/disabled
 * @property {boolean} invoicesToggle - INVOICES toggle on or off
 * @property {string} referralInfo - place where it was referred from {pricing plans go to cta, eCom settings, etc}
 * @property {string} isCustomCheckoutFormEnabled - Identification of custom checkout form
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @return {LogObject<pricingPlansTaxSettingsSavedParams>} LogObject<pricingPlansTaxSettingsSavedParams>
 */
export function pricingPlansTaxSettingsSaved(params) {
    return { evid: 60, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansSettingsPanelOpened' event (evid: 61)
 *
 * Event description: Sent when pricing plans settings are opened within Site settings in Business Manager.
 *
 * @param {pricingPlansSettingsPanelOpenedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - place where it was referred from {pricing plans go to cta, eCom settings, etc}
 * @property {GUID} biToken - msid
 * @property {string} tabName - name of the tab. For ecom version tabs no longer exist.
 * @property {string} origin - dashboard/editor
 * @property {string} checkoutVersion - Indicates checkout version used by the site. Pricing Plans; ECOM
 * @return {LogObject<pricingPlansSettingsPanelOpenedParams>} LogObject<pricingPlansSettingsPanelOpenedParams>
 */
export function pricingPlansSettingsPanelOpened(params) {
    return { evid: 61, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansSettingsClick' event (evid: 62)
 *
 * Event description: sent when "manage pricing plans" option is clicked form Manage Panel
 *
 * @param {pricingPlansSettingsClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @return {LogObject<pricingPlansSettingsClickParams>} LogObject<pricingPlansSettingsClickParams>
 */
export function pricingPlansSettingsClick(params) {
    return { evid: 62, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansError' event (evid: 63)
 *
 * Event description: sent when error is fired anywhere within Pricing Plans app
 *
 * @param {pricingPlansErrorParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {string} errorMsg - error message text
 * @return {LogObject<pricingPlansErrorParams>} LogObject<pricingPlansErrorParams>
 */
export function pricingPlansError(params) {
    return { evid: 63, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansInvoicesToggleSwitch' event (evid: 64)
 *
 * Event description: Sent on PP Invoices configuration.
 *
 * @param {pricingPlansInvoicesToggleSwitchParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {string} value - toggle_on, toggle_of, cancel, add
 * @return {LogObject<pricingPlansInvoicesToggleSwitchParams>} LogObject<pricingPlansInvoicesToggleSwitchParams>
 */
export function pricingPlansInvoicesToggleSwitch(params) {
    return { evid: 64, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ppMiscClick' event (evid: 65)
 *
 * Event description: Buttons/links/etc clicks at random places.
 *
 * @param {ppMiscClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - identifier of a button, link
 * @property {GUID} biToken - msid
 * @return {LogObject<ppMiscClickParams>} LogObject<ppMiscClickParams>
 */
export function ppMiscClick(params) {
    return { evid: 65, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetSettingsAddOnsModalActions' event (evid: 66)
 *
 * Event description: Actions with Widget settings add-ons modal
 *
 * @param {widgetSettingsAddOnsModalActionsParams} params - The parameters for this event
 * @property {GUID} appInstanceId - App instance ID
 * @property {GUID} biToken - bi Token
 * @property {string} widgetType - the widget type
 * @property {string} page_id - page ID
 * @property {string} pageName - name of the page that widget was added to
 * @property {string} action - modal_open, add_intent, add_confirm
 * @return {LogObject<widgetSettingsAddOnsModalActionsParams>} LogObject<widgetSettingsAddOnsModalActionsParams>
 */
export function widgetSettingsAddOnsModalActions(params) {
    return { evid: 66, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'subscriptionInvoicesAction' event (evid: 67)
 *
 * Event description: Invoices actions at Single Subscription
 *
 * @param {subscriptionInvoicesActionParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} subscriptionId - subscription_id
 * @property {GUID} invoiceId - invoice_id
 * @property {string} action - preview, ...
 * @return {LogObject<subscriptionInvoicesActionParams>} LogObject<subscriptionInvoicesActionParams>
 */
export function subscriptionInvoicesAction(params) {
    return { evid: 67, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'offlineFlowChangeStartDate' event (evid: 68)
 *
 * Event description: When the user (owner) change start date
 *
 * @param {offlineFlowChangeStartDateParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} planGuid - plan guid
 * @property {GUID} biToken - msid
 * @property {boolean} status - T-Paid, F-Unpaid
 * @property {GUID} site_member_id - contact_id to whom plan was sold
 * @property {DateTime} startDate - order start date
 * @return {LogObject<offlineFlowChangeStartDateParams>} LogObject<offlineFlowChangeStartDateParams>
 */
export function offlineFlowChangeStartDate(params) {
    return { evid: 68, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planClickOneApp' event (evid: 69)
 *
 * Event description: sent when UoU clicks plan
 *
 * @param {planClickOneAppParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid
 * @property {string} role - {owner,member,visitor}
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<planClickOneAppParams>} LogObject<planClickOneAppParams>
 */
export function planClickOneApp(params) {
    return { evid: 69, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansInstallationStage' event (evid: 71)
 *
 * Event description: The event is designed to track performance in various stages of installation.
 *
 * @param {pricingPlansInstallationStageParams} params - The parameters for this event
 * @property {GUID} appInstanceId - instance id of an app getting installed
 * @property {string} stage - Stages of installation between initiation and completion. (called/initiated/completed)
 * @property {string} installationType - installation_type
 * @property {GUID} biToken - msid
 * @property {GUID} appId - app id of an app that is getting installed
 * @return {LogObject<pricingPlansInstallationStageParams>} LogObject<pricingPlansInstallationStageParams>
 */
export function pricingPlansInstallationStage(params) {
    return { evid: 71, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetPlanCardCarouselSettingsSaved' event (evid: 72)
 *
 * Event description: Widget - Plan card carousel - Settings saved.
 *
 * @param {widgetPlanCardCarouselSettingsSavedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - App instance ID
 * @property {boolean} benefitsDisplayed - Enabled(True)/Disabled(False)
 * @property {string} widgetId - Widget ID
 * @property {number} plans_count - number of enabled plans
 * @property {GUID} biToken - msid
 * @property {boolean} sectionTitleState - Enabled(True)/Disabled(False)
 * @property {string} widgetType - WidgetType
 * @property {string} planIDs - Enabled plans guids
 * @property {string} sectionLocationTab - About/Pricing
 * @return {LogObject<widgetPlanCardCarouselSettingsSavedParams>} LogObject<widgetPlanCardCarouselSettingsSavedParams>
 */
export function widgetPlanCardCarouselSettingsSaved(params) {
    return { evid: 72, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'editorPlanSettingsSettingsPanelChanges' event (evid: 73)
 *
 * Event description: Fired when there are changes made in the plan settings on the editor.
 *
 * @param {editorPlanSettingsSettingsPanelChangesParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - msid
 * @property {string} fieldName - Title alignment/Subtitle alignment/Disclaimer alignment/Benefits bullets/etc.
 * @property {string} settingsType - Layout/Display/Text/Design/...
 * @property {string} value - The value that user selected
 * @return {LogObject<editorPlanSettingsSettingsPanelChangesParams>} LogObject<editorPlanSettingsSettingsPanelChangesParams>
 */
export function editorPlanSettingsSettingsPanelChanges(params) {
    return { evid: 73, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'editorPlanSettingsContactSupportClick' event (evid: 74)
 *
 * Event description: Fired when users click on links to contact support on the editor plans settings.
 *
 * @param {editorPlanSettingsContactSupportClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - msid
 * @property {string} type - Send us an email / Contact customer Care
 * @return {LogObject<editorPlanSettingsContactSupportClickParams>} LogObject<editorPlanSettingsContactSupportClickParams>
 */
export function editorPlanSettingsContactSupportClick(params) {
    return { evid: 74, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createCouponClick' event (evid: 75)
 *
 * Event description: Sent when user clicks on create coupon action
 *
 * @param {createCouponClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - plan_guid if it was clicked on specific plan
 * @property {GUID} biToken - metasite id
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} formOrigin - where it was clicked from e.g. editor settings
 * @return {LogObject<createCouponClickParams>} LogObject<createCouponClickParams>
 */
export function createCouponClick(params) {
    return { evid: 75, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ppToastClick' event (evid: 76)
 *
 * Event description: Sent when user clicks on toast
 *
 * @param {ppToastClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - pricing plans app instance id
 * @property {string} referralInfo - identifier where toast was shown
 * @property {string} toastValue - toast action text
 * @property {GUID} biToken - msid
 * @property {string} toastName - Toast name or text in the toast
 * @return {LogObject<ppToastClickParams>} LogObject<ppToastClickParams>
 */
export function ppToastClick(params) {
    return { evid: 76, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouActionAtCheckout' event (evid: 77)
 *
 * Event description: The event is fired when UoU action is performed at the checkout.
 *
 * @param {uouActionAtCheckoutParams} params - The parameters for this event
 * @property {GUID} appInstanceId - App instance ID
 * @property {GUID} visitorId - Visitor ID
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - msid
 * @property {string} couponCode - coupon_code
 * @property {number} planID - planID
 * @property {GUID} couponId - Coupon ID if coupon is used
 * @property {string} action - Name of the action. (Apply/Remove renamed to apply_coupon/remove_coupon)
 * @property {GUID} siteMemberId - Site member ID
 * @return {LogObject<uouActionAtCheckoutParams>} LogObject<uouActionAtCheckoutParams>
 */
export function uouActionAtCheckout(params) {
    return { evid: 77, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'couponErrorAtCheckout' event (evid: 78)
 *
 * Event description: Coupon error at checkout.
 *
 * @param {couponErrorAtCheckoutParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - bi_token
 * @property {string} couponCode - entered by the UoU
 * @property {string} errorField - Error message
 * @property {GUID} site_member_id - site member ID
 * @property {number} planID - planID
 * @property {string} action - Apply/Remove
 * @return {LogObject<couponErrorAtCheckoutParams>} LogObject<couponErrorAtCheckoutParams>
 */
export function couponErrorAtCheckout(params) {
    return { evid: 78, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'exitPricingPlansForm' event (evid: 79)
 *
 * Event description: sent when User clicks on exiting Pricing Plans form (x, cancel or back)
 *
 * @param {exitPricingPlansFormParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} planTitle - plan title
 * @property {number} duration - plan duration in months
 * @property {boolean} allowCancel - allow subscriptions cancellations: true/false
 * @property {GUID} planGuid - plan ID
 * @property {GUID} biToken - metasite ID
 * @property {number} purchaseLimit - limit of purchases for plan
 * @property {string} paymentMode - {one time, monthly}
 * @property {number} price - price
 * @property {boolean} isTaglineFilled - tag line filled {T/F}
 * @property {boolean} hasDisclaimer - true if the disclaimer is used; false otherwise
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {number} numOfBenefits - number of benefits defined by the user (Perks)
 * @property {string} currency - currency
 * @property {string} durationUnit - weeks, months, years
 * @property {number} freeTrialDays - trial duration in days
 * @property {string} value - X, Cancel or Back
 * @property {boolean} allowCustomStart - allowing custom start date: true/false
 * @property {string} formOrigin - indicates where the form was opened {bookings, blog, events etc)
 * @return {LogObject<exitPricingPlansFormParams>} LogObject<exitPricingPlansFormParams>
 */
export function exitPricingPlansForm(params) {
    return { evid: 79, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planSaveClick' event (evid: 8)
 *
 * Event description: sent when the user clicks on 'save plan'
 *
 * @param {planSaveClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} planTitle - plan title
 * @property {string} integratedVerticals - array of verticals that are being integrated
 * @property {number} duration - plan duration in months
 * @property {string} referralInfo - referral info
 * @property {string} billingdurationUnit - billing duration unit type (week/month/year)
 * @property {string} planPricingType - one of: subscription, single payment for duration, single payment unlimited
 * @property {boolean} allowCancel - allow subscriptions cancellations: true/false
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - metasite id
 * @property {boolean} disclaimerPolicyCheckbox - Plan policy (disclaimer) enabled/disabled
 * @property {boolean} customThankYouEnabled - Custom thank you page enabled
 * @property {number} purchaseLimit - limit  of purchases for plan
 * @property {string} paymentMode - {one time, monthly}
 * @property {number} price - plan price
 * @property {string} setupFeeName - Setup fee name. Since Aug 2023
 * @property {boolean} isTaglineFilled - tag line filled {T/F}
 * @property {string} feeAmount - fee amount
 * @property {GUID} customCheckoutFormGuid - If custom checkout form enabled at plan level. Custom form guid links to table:
 * @property {string} payment - price
 * @property {number} billingcyclecount - number of billing cycles
 * @property {boolean} hasDisclaimer - true if the disclaimer is used; false otherwise
 * @property {string} origin - {paid plans, bookings,pages} or other relevant vertical
 * @property {number} numOfBenefits - number of benefits defined by the user
 * @property {string} currency - currency
 * @property {string} durationUnit - weeks, months, years
 * @property {string} form_type - Form type defined by initiating vertical. Starting from Q3 2022
 * @property {number} freeTrialDays - trial duration in days
 * @property {boolean} disclaimerCheckedByDefault - Plan policy (disclaimer) value checked by default in checkout enabled/disabled
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {number} billingDurationCount - billing duration count
 * @property {string} formFields - Form configuration (fields) that is loaded with the form. Null values stand for standard form, with standard fields. Starting from Q3 2022
 * @property {boolean} allowCustomStart - allowing custom start date: true/false
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr)
 * @return {LogObject<planSaveClickParams>} LogObject<planSaveClickParams>
 */
export function planSaveClick(params) {
    return { evid: 8, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnPricingPlansFormField' event (evid: 80)
 *
 * Event description: sent when User clicks on Pricing Plans form fields (Plan Name, Tagline, Benefits,Free Plan,...)
 *
 * @param {clickOnPricingPlansFormFieldParams} params - The parameters for this event
 * @property {GUID} appInstanceId - appInstanceId
 * @property {GUID} planGuid - plan ID
 * @property {GUID} biToken - metasite ID
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} value_entered - particular text was written or chosen in particular field
 * @property {string} value - Clicked Form field
 * @property {string} formOrigin - indicates where the form was opened {bookings, blog, events etc)
 * @return {LogObject<clickOnPricingPlansFormFieldParams>} LogObject<clickOnPricingPlansFormFieldParams>
 */
export function clickOnPricingPlansFormField(params) {
    return { evid: 80, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansAutopiliotLoadStatusesServerless' event (evid: 802)
 *
 * Event description: Pricing Plans - Autopilot load statuses - Serverless.
 *
 * @param {pricingPlansAutopiliotLoadStatusesServerlessParams} params - The parameters for this event
 * @property {string} stage - Stages of installation between initiation and completion. (called/initiated/completed)
 * @property {GUID} appInstanceId - Instance of an app that initialised the installation
 * @property {GUID} biToken - msid
 * @property {GUID} appId - Id of an app that initialised the installation
 * @return {LogObject<pricingPlansAutopiliotLoadStatusesServerlessParams>} LogObject<pricingPlansAutopiliotLoadStatusesServerlessParams>
 */
export function pricingPlansAutopiliotLoadStatusesServerless(params) {
    return { evid: 802, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createPlanClickUnsuccessful' event (evid: 81)
 *
 * Event description: Sent when the users click 'create a plan', but are unsuccessful to proceed further due to a reason listed in the event.
 *
 * @param {createPlanClickUnsuccessfulParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referrer info
 * @property {GUID} biToken - metasite id
 * @property {string} errorReason - Reason (i.e. more than 100 plans, etc.)
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr, etc)
 * @return {LogObject<createPlanClickUnsuccessfulParams>} LogObject<createPlanClickUnsuccessfulParams>
 */
export function createPlanClickUnsuccessful(params) {
    return { evid: 81, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planFormSelectionModalOpened' event (evid: 82)
 *
 * Event description: Sent when plan form selection modal is opened. Link to BI events: https://docs.google.com/presentation/d/1FUvgkKBfccuDpjS5QIZ_yw-kaKjzOJLA2POLK9Qo0tI/edit#slide=id.g167e432e17b_0_124
 *
 * @param {planFormSelectionModalOpenedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - metasite id
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} formOrigin - indicates where the form was opened {bookings, blog, forum  etc)
 * @return {LogObject<planFormSelectionModalOpenedParams>} LogObject<planFormSelectionModalOpenedParams>
 */
export function planFormSelectionModalOpened(params) {
    return { evid: 82, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planFormSelectionModalClick' event (evid: 83)
 *
 * Event description: Sent when plan form selection modal is interacted with. Link to BI events: https://docs.google.com/presentation/d/1FUvgkKBfccuDpjS5QIZ_yw-kaKjzOJLA2POLK9Qo0tI/edit#slide=id.g167e432e17b_0_124
 *
 * @param {planFormSelectionModalClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - metasite id
 * @property {string} origin - business manager open origin {editor, dashboard, etc}
 * @property {string} form_type - Form type defined by initiating vertical. Starting from Q3 2022
 * @property {string} formOrigin - indicates where the form was opened {bookings, blog, forum  etc)
 * @return {LogObject<planFormSelectionModalClickParams>} LogObject<planFormSelectionModalClickParams>
 */
export function planFormSelectionModalClick(params) {
    return { evid: 83, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'editCustomSection' event (evid: 84)
 *
 * Event description: sent in order to track custom section components
 *
 * @param {editCustomSectionParams} params - The parameters for this event
 * @property {GUID} appInstanceId - App instance ID
 * @property {string} actionResultReason - Reason of action result
 * @property {GUID} biToken - Metasite ID
 * @property {string} componentId - Component ID
 * @property {string} planType - Type of plan
 * @property {string} actionResult - Result of action
 * @property {string} actionType - Type of action
 * @property {GUID} planId - ID of plan
 * @return {LogObject<editCustomSectionParams>} LogObject<editCustomSectionParams>
 */
export function editCustomSection(params) {
    return { evid: 84, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiAssistantPlanDescriptionSrc92Evid85' event (evid: 85)
 *
 * Event description: The purpose of this BI events is to store plan descriptions that ai assistant suggests
 *
 * @param {aiAssistantPlanDescriptionSrc92Evid85Params} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - referral_info
 * @property {GUID} biToken - msid
 * @property {string} origin - origin
 * @property {string} formOrigin - form_origin
 * @property {GUID} plan_guid - Pricing Plan guid.
 * @property {string} actionName - Action name
 * @property {string} AiGeneratedValue - AI generated value. (Left blank is action did not lead to suggestion)
 * @return {LogObject<aiAssistantPlanDescriptionSrc92Evid85Params>} LogObject<aiAssistantPlanDescriptionSrc92Evid85Params>
 */
export function aiAssistantPlanDescriptionSrc92Evid85(params) {
    return { evid: 85, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansGuestCheckoutToggleSwitch' event (evid: 86)
 *
 * Event description: sent on Guest checkout toggle switch by User. This indicates intent to add/change Guest checkout settings.
 *
 * @param {pricingPlansGuestCheckoutToggleSwitchParams} params - The parameters for this event
 * @property {GUID} appInstanceId - GUID
 * @property {GUID} biToken - BI Token
 * @property {string} value - {on/off}
 * @return {LogObject<pricingPlansGuestCheckoutToggleSwitchParams>} LogObject<pricingPlansGuestCheckoutToggleSwitchParams>
 */
export function pricingPlansGuestCheckoutToggleSwitch(params) {
    return { evid: 86, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pricingPlansUouCheckoutError' event (evid: 87)
 *
 * Event description: Event fires when the error popup loads.
 * Design:
 * https://www.figma.com/file/tWkPGKOV7bPol0BjN0PEcY/Live-Site-Checkout?node-id=2612%3A254924
 *
 * @param {pricingPlansUouCheckoutErrorParams} params - The parameters for this event
 * @property {GUID} appInstanceId - App instance ID
 * @property {GUID} planGuid - Plan guid
 * @property {GUID} biToken - BiToken
 * @property {string} errorName - Error name
 * @return {LogObject<pricingPlansUouCheckoutErrorParams>} LogObject<pricingPlansUouCheckoutErrorParams>
 */
export function pricingPlansUouCheckoutError(params) {
    return { evid: 87, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planFormThankYouMessageEditor' event (evid: 88)
 *
 * Event description: Sent when user action regarding the "Thank you message" editor is made.
 *
 * @param {planFormThankYouMessageEditorParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referral info
 * @property {GUID} planGuid - Plan guid if exists
 * @property {GUID} biToken - metasite id
 * @property {string} origin - dashboard/editor
 * @property {string} form_type - Form type defined by initiating vertical. Starting from Q3 2022. null = default_form
 * @property {string} formFields - Form configuration (fields) that is loaded with the form. Null values stand for standard form, with standard fields. Starting from Q3 2022
 * @property {string} action - load / save / ...
 * @property {boolean} is_archived - indicates if the plan viewed is archived
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr, etc)
 * @return {LogObject<planFormThankYouMessageEditorParams>} LogObject<planFormThankYouMessageEditorParams>
 */
export function planFormThankYouMessageEditor(params) {
    return { evid: 88, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'customCheckoutForm' event (evid: 89)
 *
 * Event description: Sent when user action regarding the "Custom checkout form" is made. Lin to BI events: https://docs.google.com/presentation/d/1yd6X6E41nRgIInga6Rwz2gn62fP0qdpI4SUqo1cBDPw/edit#slide=id.g1576b3ffbf0_0_69
 *
 * @param {customCheckoutFormParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - referral info
 * @property {GUID} planGuid - Plan guid if exists
 * @property {GUID} biToken - msid
 * @property {GUID} customCheckoutFormGuid - If exists form_guid that links to
 * @property {string} origin - dashboard/editor
 * @property {string} action - add_click / create_form_fields / delete_form / delete_form_confirm
 * @property {boolean} is_archived - indicates if the plan viewed is archived
 * @property {string} formOrigin - Origin where it was opened from
 * @return {LogObject<customCheckoutFormParams>} LogObject<customCheckoutFormParams>
 */
export function customCheckoutForm(params) {
    return { evid: 89, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planCreated' event (evid: 9)
 *
 * Event description: sent when a plan was created SF
 *
 * @param {planCreatedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} planTitle - plan title
 * @property {string} integratedVerticals - array of verticals that are being integrated
 * @property {number} duration - plan duration in months
 * @property {string} referralInfo - referral info
 * @property {string} billingdurationUnit - billing duration unit type (week/month/year)
 * @property {string} galleryCardTemplateID - identification of gallery card that user selected
 * @property {string} planPricingType - one of: subscription, single payment for duration, single payment unlimited
 * @property {boolean} allowCancel - allow subscriptions cancellations: true/false
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - metasite id
 * @property {boolean} disclaimerPolicyCheckbox - Plan policy (disclaimer) enabled/disabled
 * @property {boolean} customThankYouEnabled - Custom thank you page enabled
 * @property {number} purchaseLimit - limit  of purchases for plan
 * @property {string} paymentMode - {one time, monthly}
 * @property {number} price - plan price (multiplied by 100 to remove decimal)
 * @property {string} setupFeeName - Setup fee name. Since Aug 2023
 * @property {boolean} isTaglineFilled - tag line filled {T/F}
 * @property {string} feeAmount - fee amount
 * @property {GUID} customCheckoutFormGuid - If custom checkout form enabled at plan level. Custom form guid links to table:
 * @property {string} payment - price
 * @property {number} billingcyclecount - number of billing cycles
 * @property {boolean} hasDisclaimer - true if the disclaimer is used; false otherwise
 * @property {string} origin - {paid plans, bookings,pages} or other relevant vertical
 * @property {number} numOfBenefits - number of benefits defined by the user
 * @property {string} currency - currency
 * @property {string} durationUnit - weeks, months, years
 * @property {string} form_type - Form type defined by initiating vertical. Starting from Q3 2022
 * @property {number} freeTrialDays - trial duration in days
 * @property {boolean} disclaimerCheckedByDefault - Plan policy (disclaimer) value checked by default in checkout enabled/disabled
 * @property {number} planID - plan_id
 * @property {number} setupFeeAmount - Setup fee amount. Since Aug 2023
 * @property {number} billingDurationCount - billing duration count
 * @property {string} formFields - Form configuration (fields) that is loaded with the form. Null values stand for standard form, with standard fields. Starting from Q3 2022
 * @property {boolean} allowCustomStart - allowing custom start date: true/false
 * @property {string} formOrigin - indicates where the plan form was opened from {bookings modal, bizmgr)
 * @return {LogObject<planCreatedParams>} LogObject<planCreatedParams>
 */
export function planCreated(params) {
    return { evid: 9, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'planWidgetSettingsEditor' event (evid: 90)
 *
 * Event description: Sent when changes to the plan widget are made
 * BI Events: https://docs.google.com/presentation/d/1pJ0BHJHLAMFv-t7I5zbn3GYsE162X9RD3of7B06zKz8/edit#slide=id.g1cd230dc64d_0_47
 *
 * @param {planWidgetSettingsEditorParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} planGuid - Plan guid
 * @property {GUID} biToken - biToken
 * @property {string} panelLabel - Settings panel label name: Widget Layout / Pricing Layout / Benefits Layout ...
 * @property {string} origin - dashboard/editor
 * @property {string} value - action value: center, bottom, 15px, etc.
 * @property {string} action - action name i.e. button position, alignment, etc.
 * @return {LogObject<planWidgetSettingsEditorParams>} LogObject<planWidgetSettingsEditorParams>
 */
export function planWidgetSettingsEditor(params) {
    return { evid: 90, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'editorPlanSettingsPanelClick' event (evid: 91)
 *
 * Event description: Sent when a user clicks on the plan settings panel.
 *
 * @param {editorPlanSettingsPanelClickParams} params - The parameters for this event
 * @property {GUID} appInstanceId - instance
 * @property {GUID} biToken - token
 * @property {string} tabName - Name of the tab that is clicked on
 * @return {LogObject<editorPlanSettingsPanelClickParams>} LogObject<editorPlanSettingsPanelClickParams>
 */
export function editorPlanSettingsPanelClick(params) {
    return { evid: 91, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userOnboardingChecklistLoaded' event (evid: 92)
 *
 * Event description: Sent when user onboarding checklist is loaded. If all items are completed banner type = 'Success banner', otherwise = 'Checklist banner'.
 *
 * @param {userOnboardingChecklistLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - instance
 * @property {string} onboardingStatus - List of steps remaining. If all completed value is 'Completed'. If null - field was not yet required.
 * @property {GUID} biToken - token
 * @property {string} bannerType - Checklist banner / Success banner
 * @property {string} onboardingSteps - Complete list of steps in the banner
 * @return {LogObject<userOnboardingChecklistLoadedParams>} LogObject<userOnboardingChecklistLoadedParams>
 */
export function userOnboardingChecklistLoaded(params) {
    return { evid: 92, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userOnboardingChecklistAction' event (evid: 93)
 *
 * Event description: Sent when user interacts with onboarding checklist.
 *
 * @param {userOnboardingChecklistActionParams} params - The parameters for this event
 * @property {GUID} appInstanceId - instance
 * @property {string} onboardingStatus - List of steps remaining. If all completed value is 'Completed'. If null - field was not yet required.
 * @property {GUID} biToken - token
 * @property {string} bannerType - Checklist banner / Success banner
 * @property {string} actionName - action_name
 * @property {string} onboardingSteps - onboarding_steps
 * @return {LogObject<userOnboardingChecklistActionParams>} LogObject<userOnboardingChecklistActionParams>
 */
export function userOnboardingChecklistAction(params) {
    return { evid: 93, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'welcomeScreenAction' event (evid: 94)
 *
 * Event description: Sent when a user interacts with the Welcome Screen in Business Manager.
 *
 * @param {welcomeScreenActionParams} params - The parameters for this event
 * @property {GUID} appInstanceId - instance
 * @property {GUID} biToken - token
 * @property {string} version - Welcome screen version
 * @property {string} actionName - start_now; what_are_pricing_plans
 * @return {LogObject<welcomeScreenActionParams>} LogObject<welcomeScreenActionParams>
 */
export function welcomeScreenAction(params) {
    return { evid: 94, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'disconnectedPlanBenefitsModal' event (evid: 95)
 *
 * Event description: Sent when user interacts with the disconnected plans benefits modal.
 *
 * @param {disconnectedPlanBenefitsModalParams} params - The parameters for this event
 * @property {GUID} appInstanceId - applicartion instance
 * @property {GUID} biToken - token
 * @property {string} version - benefits modal version
 * @property {string} actionName - open/close/save/error
 * @return {LogObject<disconnectedPlanBenefitsModalParams>} LogObject<disconnectedPlanBenefitsModalParams>
 */
export function disconnectedPlanBenefitsModal(params) {
    return { evid: 95, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'galleryLoaded' event (evid: 96)
 *
 * Event description: Sent when the Plan Gallery is loaded
 * Designs:
 * https://www.figma.com/file/KHla7HzYf2veoODF83c9ik/Onboarding-checklist?node-id=1-2&t=iGiddHbQ2o8we5Ii-0
 *
 * @param {galleryLoadedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - BizMgr/welcome page/...
 * @property {GUID} biToken - metasite id
 * @property {string} gallerySiteCategory - Site category used to prioritise gallery images
 * @property {string} version - identification of the design version
 * @property {string} origin - {BizMgr,Editor,other app name}
 * @property {string} galleryTemplatesList - List of plan templates presented in gallery
 * @return {LogObject<galleryLoadedParams>} LogObject<galleryLoadedParams>
 */
export function galleryLoaded(params) {
    return { evid: 96, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userFunnelGalleryItemSelected' event (evid: 97)
 *
 * Event description: Sent when a user clicks on an item in the Plan Gallery.
 * Designs:
 * https://www.figma.com/file/KHla7HzYf2veoODF83c9ik/Onboarding-checklist?node-id=1-2&t=iGiddHbQ2o8we5Ii-0
 *
 * @param {userFunnelGalleryItemSelectedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {string} referralInfo - BizMgr/welcome page/...
 * @property {string} galleryCardTemplatePricingType - information about template: subscription/onetime/etc
 * @property {string} galleryCardTemplateID - identification of gallery card that user selected
 * @property {GUID} biToken - metasite id
 * @property {number} galleryCardTemplatePeriodCount - information about template: period count
 * @property {string} gallerySiteCategory - Site category used to prioritise gallery images
 * @property {string} version - identification of the design version
 * @property {string} galleryCardTemplatePeriodUnit - information about template: Year/Month/etc.
 * @property {string} galleryCardTemplateImageID - information about template: image name
 * @property {string} origin - {BizMgr,Editor,other app name}
 * @property {string} galleryTemplatesList - List of plan templates presented in gallery
 * @property {number} galleryCardTemplateImage - information about template: image name
 * @property {number} galleryCardTemplateCycleCount - information about template: cycle count
 * @return {LogObject<userFunnelGalleryItemSelectedParams>} LogObject<userFunnelGalleryItemSelectedParams>
 */
export function userFunnelGalleryItemSelected(params) {
    return { evid: 97, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'offlineFlowConnectPaymentMethodModal' event (evid: 98)
 *
 * Event description: Event fired on interactions with connect payment modal. Designs: https://www.figma.com/file/UWvgVdO89VDHH8EXfmf8g6/Pricing-Plans%3A-MOTO?type=design&node-id=828-318013&t=TXhQqwEOsrNyoO6s-0
 *
 * @param {offlineFlowConnectPaymentMethodModalParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} referralInfo - Indicates where action was made
 * @property {GUID} planGuid - plan_guid
 * @property {GUID} biToken - biToken
 * @property {string} origin - origin
 * @property {GUID} site_member_id - site_member_id
 * @property {string} action - Open/Close/Learn more/Connect Payment Method
 * @return {LogObject<offlineFlowConnectPaymentMethodModalParams>} LogObject<offlineFlowConnectPaymentMethodModalParams>
 */
export function offlineFlowConnectPaymentMethodModal(params) {
    return { evid: 98, src: 92, endpoint: '', params };
}
/**
 * A function to create a log object for the 'settingsAction' event (evid: 99)
 *
 * Event description: Fired when action is performed in the Pricing Plans settings area. (excluding save actions. For save actions use 92:60 event)
 *
 * @param {settingsActionParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} biToken - biToken
 * @property {string} sectionName - payment methods/guest checkout/taxes/custom forms/plan policy/invoices/thank you page/
 * @property {string} tabName - Billing/Automations/Checkout/,,,
 * @property {string} action - Click/Open/Close
 * @return {LogObject<settingsActionParams>} LogObject<settingsActionParams>
 */
export function settingsAction(params) {
    return { evid: 99, src: 92, endpoint: '', params };
}
//# sourceMappingURL=index.js.map